import React, { useEffect, useState, KeyboardEvent } from "react";
import Breadcrumbs from "../common/breadcrumbs";
import Aos from "aos";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import DashboardMenu from "./common/dashboard-menu";
import { Link } from "react-router-dom";
import axiosInstance from "../../core/services/axiosInstance";
import { useAuth } from "../../core/context/AuthContext";
import BASE_URL from "../../config";
import io, { Socket } from "socket.io-client";
import AuthService from "../../core/services/authService"
import axios from 'axios';
// Define types for Conversation and Message
interface Conversation {
  _id: string;
  participants: {
    participant: string;
    participantModel: string;
  }[];
}

interface Message {
  _id: string;
  content: string;
  senderModel: string;
  createdAt: string;
  conversation: string;
}
interface FlaskResponse {
  response: {
    message: string;
    details?: Record<string, any>; 
    missing_details?: string[];   
  };
}


const UserMessages: React.FC = () => {
  const [openChat, setOpenChat] = useState<boolean>(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const { isAuthenticated } = useAuth();
  const socket: Socket = io(BASE_URL);

  const chatOpen = () => {
    setOpenChat(!openChat);
  };

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    socket.on("receive_message", (message: Message) => {
      if (message.conversation === currentConversationId) {
        setMessages((prevMessages) => [...prevMessages, message]);
      }
    });

    return () => {
      socket.off("receive_message");
    };
  }, [currentConversationId]);

  const fetchConversations = async () => {
    try {
      const response = await axiosInstance.get<Conversation[]>("/api/conversations");
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    try {
      const response = await axiosInstance.get<Message[]>(
        `/api/conversations/${conversationId}/messages`
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() === "") return;

    const userMessage: Message = {
      _id: Date.now().toString(),
      content: newMessage,
      senderModel: "client",
      createdAt: new Date().toISOString(),
      conversation: currentConversationId ?? "",
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setNewMessage("");

    try {
      const response = await axiosInstance.post<FlaskResponse>(
        "http://127.0.0.1:5000/process_reservation",
        {
          message: newMessage,
        }
      );

      const flaskResponse = response.data;

      if (flaskResponse.response) {
        const { message, details, missing_details } = flaskResponse.response;

        let systemMessageContent: string;

        if (details) {
          const client = AuthService.getUser();
          console.log("Client Info:", client);
          if (!client) {
            systemMessageContent = "Please log in to proceed with the reservation.";
            return;
          }
        
          const clientId = client.clientId;
        
          try {
            const response = await axiosInstance.post('/api/reservations/support/', {
              clientId,
              carName: details.car,
              startDate: details.startDate,
              returnDate: details.returnDate,
              sameLocationReturn: details.sameReturnLocation,
            });
        
            if (response.status === 201 && response.data.success) {
              systemMessageContent = `Reservation successful! Your car is reserved from ${details.startDate} to ${details.returnDate}.`;
            } else {
              systemMessageContent = "The reservation was processed, but something seems off. Please contact support.";
            }
          } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
              if (error.response) {
                if (error.response.status === 404) {
                  systemMessageContent = `The car "${details.car}" is not available. Please choose a different car.`;
                } else if (error.response.status === 400) {
                  systemMessageContent = "Invalid reservation dates. Please check your input and try again.";
                } else {
                  systemMessageContent = "An error occurred while processing your reservation. Please try again.";
                }
              } else if (error.request) {
                systemMessageContent = "Could not connect to the server. Please check your network and try again.";
              } else {
                systemMessageContent = "An unexpected error occurred. Please try again.";
              }
            } else {
              systemMessageContent = "A critical error occurred. Please contact support.";
            }
          }
        }
        
        else if (missing_details) {
          systemMessageContent = `${message}\n:\n- ${missing_details.join("\n- ")}`;

        }
        else {
          systemMessageContent = message;
        }

        const systemMessage: Message = {
          _id: Date.now().toString(),
          content: systemMessageContent,
          senderModel: "system",
          createdAt: new Date().toISOString(),
          conversation: currentConversationId ?? "",
        };

        setMessages((prevMessages) => [...prevMessages, systemMessage]);
      }

      socket.emit("send_message", userMessage);

    } catch (error) {
      console.error("Error sending message:", error);
    }
  };



  const supportAgentId = "674a0f9552a1ed0414bfd586";

  const startSupportConversation = async () => {
    try {
      const existingConversation = conversations.find((conversation) =>
        conversation.participants.some(
          (participant) =>
            participant.participantModel === "user" &&
            participant.participant === supportAgentId
        )
      );

      if (existingConversation) {
        setCurrentConversationId(existingConversation._id);
        fetchMessages(existingConversation._id);
      } else {
        const response = await axiosInstance.post<Conversation>("/api/conversations", {
          participantModel: "user",
          participantId: supportAgentId,
        });
        fetchConversations();
        setCurrentConversationId(response.data._id);
        fetchMessages(response.data._id);
      }
    } catch (error) {
      console.error("Error starting support conversation:", error);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <>
      <Breadcrumbs title="User Message" subtitle="User Message" />
      <DashboardMenu />
      <div className="content content-chat top-space-chat">
        <div className="container">
          <div className="content-header">
            <h4>Messages</h4>
          </div>
          <div className={`row chat-window ${openChat ? "chat-slide" : ""}`}>
            <div className="col-xl-12">
              <div className={`chat-window ${openChat ? "chat-slide" : ""}`}>
                <div className="chat-cont-left">
                  <div className="chat-header">
                    <span>Chats</span>
                  </div>
                  <div className="chat-users-list">
                    <div className="chat-scroll">
                      <Link
                        to="#"
                        className={`notify-block read-chat d-flex open-chat ${currentConversationId ? "" : "active"
                          }`}
                        onClick={startSupportConversation}
                      >
                        <div className="media-img-wrap flex-shrink-0">
                          <div className="avatar avatar-online">
                            <ImageWithBasePath
                              src="assets/img/profiles/support-avatar.png"
                              alt="User Image"
                              className="avatar-img rounded-circle"
                            />
                          </div>
                        </div>
                        <div className="media-body chat-custom flex-grow-1">
                          <div>
                            <div className="user-name">Support Team</div>
                          </div>
                          <div>
                            <div className="last-chat-time block">Start a conversation</div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="chat-cont-right">
                  <div className="chat-header">
                    <Link onClick={chatOpen} id="back_user_list" to="#" className="back-user-list">
                      <i className="feather icon-chevron-left" />
                    </Link>
                    <div className="notify-block d-flex">
                      <div className="media-img-wrap flex-shrink-0">
                        <div className="avatar avatar-online">
                          <ImageWithBasePath
                            src="assets/img/profiles/support-avatar.png"
                            alt="User Image"
                            className="avatar-img rounded-circle"
                          />
                        </div>
                      </div>
                      <div className="media-body flex-grow-1">
                        <div className="user-name">Support team</div>
                        <div className="user-status">online</div>
                      </div>
                    </div>
                  </div>
                  <div className="chat-body">
                    <div className="chat-scroll">
                      <ul className="list-unstyled">
                        {messages.map((message) => (
                          <li
                            key={message._id}
                            className={`notify-block d-flex ${message.senderModel === "client" ? "sent" : "received"
                              }`}
                          >
                            {message.senderModel === "user" && (
                              <div className="avatar flex-shrink-0">
                                <ImageWithBasePath
                                  src="assets/img/profiles/support-avatar.png"
                                  alt="User Image"
                                  className="avatar-img rounded-circle"
                                />
                              </div>
                            )}
                            <div className="media-body flex-grow-1">
                              <div className="msg-box">
                                <div>
                                  <p>{message.content}</p>
                                  <ul className="chat-msg-info">
                                    <li>
                                      <div className="chat-time">
                                        <span>
                                          {new Date(message.createdAt).toLocaleTimeString()}
                                        </span>
                                      </div>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="chat-footer">
                    <div className="input-group">
                      <input
                        type="text"
                        className="input-msg-send form-control rounded-pill"
                        placeholder="Type something"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                      />
                      <button
                        type="button"
                        className="btn msg-send-btn rounded-pill ms-2"
                        onClick={handleSendMessage}
                      >
                        <i className="fa-solid fa-paper-plane" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserMessages;
